import React from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';

import Button from '@components/Atoms/Button/Button';
import { useFeatureFlagEnabled } from '@components/Atoms/FeatureFlagGuard/hooks';
import { exportLEReportCSV } from '@apis/learnosityexams';
import { useCurrentSection } from '@reducers/sections/hooks';
import useExamReportCsvExport from '../../hooks/useExamReportCsvExport';
import { useGetActiveTestSession } from '../../hooks';
import { ReportType } from './useReportHooks';

export const EXAM_CSV_EXPORT_FEATURE = 'exam-results-csv-export';

interface ExportCsvButtonProps {
  reportType: ReportType;
}

/**
 * Queues a CSV export of one section report type. Sits in the section header's
 * action slot, so the surrounding title already names the report type. Hidden
 * entirely unless the exam-results-csv-export flag is on for the user's district.
 */
const ExportCsvButton: React.FC<ExportCsvButtonProps> = ({ reportType }) => {
  const isEnabled = useFeatureFlagEnabled({ featureName: EXAM_CSV_EXPORT_FEATURE });
  const section = useCurrentSection();
  const testSession = useGetActiveTestSession();
  const { isExporting, startExport } = useExamReportCsvExport();

  const examId = testSession?.exam?.id;
  const sectionId = section?.id;

  if (!isEnabled || !examId || !sectionId) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      size="small"
      disabled={isExporting}
      onClick={() => startExport(() => exportLEReportCSV(reportType, examId, sectionId))}
      startIcon={
        isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
      }
    >
      {isExporting ? 'Preparing CSV...' : 'Export CSV'}
    </Button>
  );
};

export default ExportCsvButton;
